import { formatBetrag, parseZeit, statusLabel } from './belegKarte';
import { NavigationsZiel, sichtbareTabIds, zielErreichbar } from './tabRollen';

/** Eine Zelle der Belegkette eines Vorgangs (Backend: GET /api/partners/{id}/akte). */
export type KettenZelle = {
  status: string;
  label: string;
  datum: string | null;
  id: number | null;
  anzahl?: number;
} | null;

export type KettenSpalte = 'angebot' | 'auftrag' | 'lieferung' | 'rechnung' | 'bezahlt';

export interface Vorgang {
  auftrag_id: number;
  auftrag_nr: string;
  kommission: string | null;
  status: string;
  auftrag_typ?: string;
  offen: boolean;
  erstellt_am: string | null;
  projekt_id: number | null;
  projekt_bezeichnung: string | null;
  kette: Record<KettenSpalte, KettenZelle>;
}

export type VorgangFilter = 'alle' | 'offen';

export const KETTEN_SPALTEN: { key: KettenSpalte; label: string }[] = [
  { key: 'angebot', label: 'Angebot' },
  { key: 'auftrag', label: 'Auftrag' },
  { key: 'lieferung', label: 'Lieferung' },
  { key: 'rechnung', label: 'Rechnung' },
  { key: 'bezahlt', label: 'Bezahlt' },
];

export function filterVorgaenge(vorgaenge: Vorgang[], filter: VorgangFilter): Vorgang[] {
  return filter === 'offen' ? vorgaenge.filter(v => v.offen) : vorgaenge;
}

const OFFEN = ['ENTWURF', 'OFFEN'];
const ABSCHLUSS = ['VERSENDET', 'ANGENOMMEN', 'BEZAHLT', 'ZUGESTELLT', 'Geliefert', 'Fakturiert', 'Abgenommen'];
const STORNIERT = ['STORNIERT', 'UEBERFAELLIG', 'Storniert'];

/** Darstellungsart einer Kettenzelle (steuert Farbe/Symbol in der Akte). */
export function zellenKind(zelle: KettenZelle): 'leer' | 'offen' | 'fortschritt' | 'abschluss' | 'storniert' {
  if (!zelle) return 'leer';
  if (OFFEN.includes(zelle.status)) return 'offen';
  if (ABSCHLUSS.includes(zelle.status)) return 'abschluss';
  if (STORNIERT.includes(zelle.status)) return 'storniert';
  return 'fortschritt';
}

/** dd.mm.yyyy; zonenlose Zeitstempel sind UTC (wie belegKarte.parseZeit). */
export function formatDatumKurz(iso: string | null | undefined): string {
  if (!iso) return '';
  const zeit = parseZeit(iso);
  if (Number.isNaN(zeit.getTime())) return '';
  return zeit.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

/** Wie formatDatumKurz, leer als „–" (Tabellenzellen). */
export function datumKurz(iso: string | null | undefined): string {
  return formatDatumKurz(iso) || '–';
}

/** „von – bis" mit „…" für ein fehlendes Ende, „–" wenn beides fehlt. */
export function zeitraum(von: string | null | undefined, bis: string | null | undefined): string {
  if (!von && !bis) return '–';
  return `${formatDatumKurz(von) || '…'} – ${formatDatumKurz(bis) || '…'}`;
}

/** Enum-Werte (GROSS) lesbar machen; Klartext-Status (z. B. „In Produktion") bleibt. */
export function statusText(status: string | null | undefined): string {
  if (!status) return '–';
  return status === status.toUpperCase() ? statusLabel(status) : status;
}

const REFERENZ_TYP_TEXT: Record<string, string> = {
  partner: 'Kunde',
  auftrag: 'Auftrag',
  ausgangsrechnung: 'Rechnung',
  bestellung: 'Bestellung',
  projekt: 'Baustelle',
};

/** Anzeigename des Dokument-Bezugs (Audit-Tabellenname -> Fachbegriff). */
export function referenzTypText(typ: string | null | undefined): string {
  if (!typ) return '–';
  return REFERENZ_TYP_TEXT[typ] ?? typ;
}

// ── Einsatz-Zeiten (Wanduhrzeit der Betriebszeitzone) ──

const WANDZEIT = /^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2}))?/;

/** Einsatz-Zeiten sind Wanduhrzeiten der Betriebszeitzone des Mandanten (Einstellungen >
    Allgemein > ZEITZONE) und ohne Zonenangabe gespeichert: anzeigen wie gespeichert,
    keine Umrechnung in die Browser-Zone. Liefert [Datum, Uhrzeit|null]. */
function wandzeitTeile(iso: string | null | undefined): [string, string | null] | null {
  if (!iso) return null;
  const m = WANDZEIT.exec(iso);
  if (!m) return null;
  return [`${m[3]}.${m[2]}.${m[1]}`, m[4] !== undefined ? `${m[4]}:${m[5]}` : null];
}

/** „20.10.2026, 08:00–10:00" (gleicher Tag), sonst „20.10.2026, 08:00 – 21.10.2026, 10:00";
    ohne Uhrzeit nur das Datum, ohne Beginn „–". */
export function einsatzZeitraum(beginn: string | null | undefined, ende: string | null | undefined): string {
  const b = wandzeitTeile(beginn);
  if (!b) return '–';
  const e = wandzeitTeile(ende);
  const bText = b[1] ? `${b[0]}, ${b[1]}` : b[0];
  if (!e || !e[1] || !b[1]) return bText;
  return e[0] === b[0] ? `${bText}–${e[1]}` : `${bText} – ${e[0]}, ${e[1]}`;
}

// ── Reiter ──

export type PartnerTyp = 'Kunde' | 'Lieferant' | 'Beides';

export type Reiter =
  | 'vorgaenge' | 'rechnungen' | 'bestellungen' | 'eingangsrechnungen'
  | 'termine' | 'baustellen' | 'konditionen' | 'dokumente' | 'verlauf';

export interface ReiterDef {
  key: Reiter;
  label: string;
  /** Nur sichtbar, wenn dieses Modul im Mandanten aktiv ist (leeres Modul-Set = noch nicht geladen). */
  modul?: string;
  /** Nur fuer Kunden bzw. nur fuer Lieferanten; ohne Angabe fuer beide Seiten. */
  seite?: 'kunde' | 'lieferant';
}

export const REITER: ReiterDef[] = [
  { key: 'vorgaenge', label: 'Vorgänge', modul: 'auftrag', seite: 'kunde' },
  { key: 'rechnungen', label: 'Rechnungen', seite: 'kunde' },
  { key: 'bestellungen', label: 'Bestellungen', modul: 'bestellung', seite: 'lieferant' },
  { key: 'eingangsrechnungen', label: 'Eingangsrechnungen', seite: 'lieferant' },
  { key: 'termine', label: 'Termine' },
  { key: 'baustellen', label: 'Baustellen', modul: 'projekt', seite: 'kunde' },
  { key: 'konditionen', label: 'Konditionen' },
  { key: 'dokumente', label: 'Dokumente' },
  { key: 'verlauf', label: 'Verlauf' },
];

/** Modul aktiv? Ein leeres Set heisst „noch nicht geladen": nichts sperren (wie ManagementPage). */
export function modulAktiv(aktiveModule: Set<string>, modul: string | undefined): boolean {
  return !modul || aktiveModule.size === 0 || aktiveModule.has(modul);
}

function seiten(typ: PartnerTyp | string): Array<'kunde' | 'lieferant'> {
  if (typ === 'Lieferant') return ['lieferant'];
  if (typ === 'Beides') return ['kunde', 'lieferant'];
  return ['kunde'];
}

/** Reiter der Akte fuer diesen Partnertyp und die aktiven Module, in Leistenreihenfolge. */
export function sichtbareReiter(typ: PartnerTyp | string, aktiveModule: Set<string>): ReiterDef[] {
  const s = seiten(typ);
  return REITER.filter(r => (!r.seite || s.includes(r.seite)) && modulAktiv(aktiveModule, r.modul));
}

/** Zielreiter für Pfeil-/Home-/End-Tasten in der Reiterleiste (WAI-ARIA Tabs, zyklisch
    ueber die sichtbaren Reiter); null für jede andere Taste. */
export function reiterNachTaste(keys: Reiter[], aktuell: Reiter, taste: string): Reiter | null {
  if (keys.length === 0) return null;
  const i = Math.max(keys.indexOf(aktuell), 0);
  switch (taste) {
    case 'ArrowRight': return keys[(i + 1) % keys.length];
    case 'ArrowLeft': return keys[(i - 1 + keys.length) % keys.length];
    case 'Home': return keys[0];
    case 'End': return keys[keys.length - 1];
    default: return null;
  }
}

/** Ueberschrift der Akte nach Partnertyp. */
export function akteTitel(typ: PartnerTyp | string | undefined): string {
  if (typ === 'Lieferant') return 'Lieferantenakte';
  if (typ === 'Beides') return 'Partnerakte';
  return 'Kundenakte';
}

// ── Navigation in Verwaltungs-Tabs ──

/** Ziel der Schnellaktion „+ Termin" und der Termin-Zeilen: die Einsatzplanung. */
export const PLANTAFEL_ZIEL: NavigationsZiel = { ansicht: 'management', tab: 'einsaetze', params: {} };
/** Ziel der Schnellaktion „+ Bestellung" (Lieferanten). */
export const BESTELLUNGEN_ZIEL: NavigationsZiel = { ansicht: 'management', tab: 'bestellungen', params: {} };

/** Ist ein Verwaltungs-Tab fuer diesen Benutzer erreichbar? Modul aktiv (leeres Set =
    noch nicht geladen, nichts sperren) UND Tab fuer seine Rollen sichtbar (leere Rollen =
    noch nicht bekannt, nichts sperren) - dieselbe Regel, nach der ManagementPage den Tab zeigt. */
export function tabErreichbar(
  ziel: NavigationsZiel, modul: string | undefined, rollen: string[], aktiveModule: Set<string>,
): boolean {
  return modulAktiv(aktiveModule, modul) && zielErreichbar(ziel, sichtbareTabIds(rollen));
}

export function plantafelErreichbar(rollen: string[], aktiveModule: Set<string>): boolean {
  return tabErreichbar(PLANTAFEL_ZIEL, 'einsatz', rollen, aktiveModule);
}

export function bestellungenErreichbar(rollen: string[], aktiveModule: Set<string>): boolean {
  return tabErreichbar(BESTELLUNGEN_ZIEL, 'bestellung', rollen, aktiveModule);
}

/** Kalendertag (yyyy-mm-dd) eines Termins für den Sprung in die Plantafel-Woche: der
    Datumsanteil des Zeitstempels, so wie die Plantafel ihre Einsätze selbst in Tage
    einsortiert (ohne Zonenrechnung). */
export function terminTag(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const m = /^(\d{4}-\d{2}-\d{2})/.exec(iso);
  return m ? m[1] : null;
}

// ── Kennzahlen-Kacheln ──

export interface AkteKennzahlen {
  offene_posten: { summe?: number; anzahl: number; anzahl_ueberfaellig: number };
  umsatz_jahr: { summe?: number; summe_netto?: number; anzahl_rechnungen: number; jahr: number };
  laufende_auftraege: { anzahl: number; anzahl_baustellen: number };
  letzter_kontakt: { zeitpunkt: string | null; art: string | null };
  einkauf?: {
    verbindlichkeiten: { summe?: number; anzahl: number; anzahl_ueberfaellig: number };
    einkauf_jahr: { summe?: number; anzahl_bestellungen: number; jahr: number };
    offene_bestellungen: { anzahl: number };
  } | null;
}

export interface Kachel { label: string; wert: string; sub: string }

/** Die Kopf-Kacheln der Akte: Kundensicht (offene Posten, Umsatz brutto mit netto, laufende
    Auftraege), Lieferantensicht (Verbindlichkeiten, Einkauf, offene Bestellungen) und der
    letzte Kontakt; reine Lieferanten sehen die Kundenkacheln nicht. Ohne Preisrecht fehlen
    die Summen, dann zeigen die Kacheln Anzahlen. */
export function kennzahlKacheln(k: AkteKennzahlen, preiseSichtbar: boolean, typ: PartnerTyp | string): Kachel[] {
  const kacheln: Kachel[] = [];
  const s = seiten(typ);
  if (s.includes('kunde')) {
    const { offene_posten: op, umsatz_jahr: u } = k;
    const umsatzMitBetrag = preiseSichtbar && u.summe !== undefined;
    kacheln.push({
      label: 'Offene Posten',
      wert: preiseSichtbar && op.summe !== undefined ? formatBetrag(op.summe) : `${op.anzahl} offen`,
      sub: `${op.anzahl_ueberfaellig} überfällig`,
    }, {
      label: `Umsatz ${u.jahr}`,
      wert: umsatzMitBetrag ? formatBetrag(u.summe) : `${u.anzahl_rechnungen} Rechnungen`,
      sub: umsatzMitBetrag
        ? [u.summe_netto !== undefined ? `netto ${formatBetrag(u.summe_netto)}` : '', `${u.anzahl_rechnungen} Rechnungen`]
            .filter(Boolean).join(' · ')
        : 'brutto',
    }, {
      label: 'Laufende Aufträge',
      wert: String(k.laufende_auftraege.anzahl),
      sub: `${k.laufende_auftraege.anzahl_baustellen} Baustellen`,
    });
  }
  if (s.includes('lieferant') && k.einkauf) {
    const { verbindlichkeiten: v, einkauf_jahr: e, offene_bestellungen: ob } = k.einkauf;
    kacheln.push({
      label: 'Verbindlichkeiten',
      wert: preiseSichtbar && v.summe !== undefined ? formatBetrag(v.summe) : `${v.anzahl} offen`,
      sub: `${v.anzahl_ueberfaellig} überfällig`,
    }, {
      label: `Einkauf ${e.jahr}`,
      wert: preiseSichtbar && e.summe !== undefined ? formatBetrag(e.summe) : `${e.anzahl_bestellungen} Bestellungen`,
      sub: preiseSichtbar && e.summe !== undefined ? `${e.anzahl_bestellungen} Bestellungen` : '',
    }, {
      label: 'Offene Bestellungen',
      wert: String(ob.anzahl),
      sub: '',
    });
  }
  kacheln.push({
    label: 'Letzter Kontakt',
    wert: datumKurz(k.letzter_kontakt.zeitpunkt),
    sub: k.letzter_kontakt.art || '',
  });
  return kacheln;
}
